import { useI18n } from "../lib/i18n.jsx";
import { CONTACT, ORGANIZER } from "../lib/content.js";
import { trackContact } from "../lib/analytics.js";
import { Link } from "react-router-dom";
import { reopenCookieSettings } from "./CookieConsent.jsx";
import {
  Instagram,
  Facebook,
  Youtube,
  Mail,
  MapPin,
  Phone,
  Clock,
  FileText,
} from "lucide-react";

// Lucide doesn't ship a TikTok glyph — minimal inline SVG matches the icon set.
const TikTokIcon = ({ className = "w-4 h-4" }) => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    fill="currentColor"
    className={className}
  >
    <path d="M16.5 3a5.5 5.5 0 0 0 4.5 4.5v3a8.5 8.5 0 0 1-4.5-1.3v6.8a6 6 0 1 1-6-6c.34 0 .67.03 1 .09v3.18a3 3 0 1 0 2 2.83V3h3z" />
  </svg>
);

const SOCIALS = [
  { id: "instagram", url: CONTACT.instagram, label: "Instagram", Icon: Instagram },
  { id: "facebook", url: CONTACT.facebook, label: "Facebook", Icon: Facebook },
  { id: "tiktok", url: CONTACT.tiktok, label: "TikTok", Icon: TikTokIcon },
  { id: "youtube", url: CONTACT.youtube, label: "YouTube", Icon: Youtube },
];

export default function Footer() {
  const { t } = useI18n();

  const colHeader =
    "font-display font-semibold text-xs uppercase tracking-[0.18em] text-cream/50 mb-4";

  return (
    <footer
      data-testid="site-footer"
      className="bg-winter text-cream"
    >
      <div className="max-w-[1300px] mx-auto px-5 md:px-8 pt-16 md:pt-20 pb-10">
        {/* 4-column info grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Organizator */}
          <div data-testid="footer-organizer">
            <p className={colHeader}>{t.footer.organizer}</p>
            <ul className="space-y-2 text-sm text-cream/85">
              <li className="font-semibold">{ORGANIZER.name}</li>
              <li>
                <span className="text-cream/55">{t.footer.tax_id}: </span>
                {ORGANIZER.nip}
              </li>
              <li>
                <span className="text-cream/55">{t.footer.regon}: </span>
                {ORGANIZER.regon}
              </li>
              <li>
                <span className="text-cream/55">{t.footer.krs}: </span>
                {ORGANIZER.krs}
              </li>
              <li className="pt-2">
                <a
                  href={ORGANIZER.registry_pdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-organizer-pdf"
                  className="inline-flex items-start gap-2 text-cream/85 hover:text-summer transition-colors leading-snug"
                >
                  <FileText className="w-3.5 h-3.5 mt-0.5 shrink-0 text-summer" />
                  <span className="text-xs underline-offset-2 hover:underline">
                    {t.footer.registry_link}
                  </span>
                </a>
              </li>
            </ul>
          </div>

          {/* Adres */}
          <div data-testid="footer-address">
            <p className={colHeader}>{t.footer.address_label}</p>
            <ul className="space-y-1.5 text-sm text-cream/85">
              <li className="font-semibold">{ORGANIZER.brand}</li>
              <li className="flex items-start gap-2 text-cream/85">
                <MapPin className="w-3.5 h-3.5 mt-1 shrink-0 text-summer" />
                <span>
                  {ORGANIZER.street}
                  <br />
                  {ORGANIZER.city}
                  <br />
                  {ORGANIZER.country}
                </span>
              </li>
            </ul>
          </div>

          {/* Kontakt */}
          <div data-testid="footer-contact">
            <p className={colHeader}>{t.footer.contact_label}</p>
            <ul className="space-y-2.5 text-sm text-cream/85">
              <li className="font-semibold">{t.footer.office_label}</li>
              <li className="flex items-center gap-2">
                <Clock className="w-3.5 h-3.5 text-summer shrink-0" />
                <span>{CONTACT.phone_hours}</span>
              </li>
              <li>
                <a
                  href={`tel:${CONTACT.phone.replace(/\s/g, "")}`}
                  data-testid="footer-phone"
                  onClick={() => trackContact("phone")}
                  className="flex items-center gap-2 hover:text-summer transition-colors"
                >
                  <Phone className="w-3.5 h-3.5 text-summer shrink-0" />
                  <span>{CONTACT.phone}</span>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${CONTACT.email}`}
                  data-testid="footer-email"
                  onClick={() => trackContact("email")}
                  className="flex items-center gap-2 hover:text-summer transition-colors break-all"
                >
                  <Mail className="w-3.5 h-3.5 text-summer shrink-0" />
                  <span>{CONTACT.email}</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Śledź nas */}
          <div data-testid="footer-social">
            <p className={colHeader}>{t.footer.social_label}</p>
            <div className="flex items-center gap-2.5" data-testid="footer-social-bar">
              {SOCIALS.map(({ id, url, label, Icon }) => (
                <a
                  key={id}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`footer-social-${id}`}
                  className="w-10 h-10 rounded-full bg-cream/10 hover:bg-summer text-cream flex items-center justify-center transition-colors"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom strip */}
        <div className="border-t border-cream/10 pt-8 flex flex-col md:flex-row md:items-center justify-between gap-3 text-xs text-cream/50">
          <span>© {new Date().getFullYear()} {t.footer.brand}. {t.footer.rights}</span>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <Link
              to={t.lang === "en" ? "/privacy-policy" : "/polityka-prywatnosci"}
              className="hover:text-summer transition-colors"
              data-testid="footer-privacy"
            >
              {t.footer.privacy_link}
            </Link>
            <button
              type="button"
              onClick={reopenCookieSettings}
              className="hover:text-summer transition-colors text-left"
              data-testid="footer-cookie-settings"
            >
              {t.footer.cookie_settings}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
